import type { Transition } from "motion/react";

/**
 * The button's entry motion, "metal" (Sebastian, 2026-10-21: "behave like
 * metal — no scaling or wobbly, swiftly move in from under the chip and
 * stop with weight"; "more faster movement on entry, to sell the heavy
 * sharp feeling"; then "use this type of entry on the other campaign types
 * as well, stagger its different elements a bit"): each piece slides out
 * from under the coin along x only — a heavy mass on a very stiff spring,
 * all but critically damped, so it shoots in and stops dead (~0.15s) — its
 * fade done in the first beat, so the move reads as a slide. One source for
 * every type's content and the shared set.
 */

/** Where a piece starts: its whole width back, under the coin (it grows
 *  out of the coin's centre), so nothing of it shows beside the coin before
 *  it moves (Sebastian, 2026-10-21: "you can see the start of the entry on
 *  the right side of the coin when switching"). And its start pose. */
export const METAL_FROM_X = "-100%";
export const METAL_FROM = { x: METAL_FROM_X, opacity: 0 } as const;
/** The beat between staggered pieces (s). */
export const METAL_STAGGER = 0.06;

/** The slide's transition, after `delay` seconds. */
export function metalArrive(delay = 0): Transition {
  return {
    x: { type: "spring", stiffness: 2200, damping: 135, mass: 2.2, delay },
    opacity: { duration: 0.07, delay },
  };
}

/** Leaving (Sebastian, 2026-10-21: "a reversed entry"; then "yes" to it for
 *  every type): back under the coin along x on the same spring, after
 *  `delay` seconds, at full ink — the coin and the set's clip hide it. */
export function metalLeave(delay = 0) {
  return {
    x: METAL_FROM_X,
    transition: {
      x: {
        type: "spring",
        stiffness: 2200,
        damping: 135,
        mass: 2.2,
        delay,
      },
    },
  } as const;
}

/** The same spring for an imperative `animate()` (one value set). */
export const METAL_SPRING = {
  type: "spring",
  stiffness: 2200,
  damping: 135,
  mass: 2.2,
} as const;
